'use client';

import { isMac } from '@/hooks/platform';
import { logger } from '@/utils/logger/logger';
import {
  clearAnalyticsSession,
  desktopDictationAllGrantedAtom,
  dictateOnboardingLiteWizard,
  dictateOnboardingWizard,
  PRE_AUTH_STEP_SET,
  useDictateOnboardingAnalytics,
  type DictateLiteStep,
  type DictateStep,
} from '@heidi/dictate';
import { useWizard } from '@heidi/flow/react';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getInitialDictateFormData,
  type DictateFormData,
  type DictateOnboardingContextValue,
  type DictateOnboardingVariant,
  type DictateStepCompletedExtra,
  type GoToNextStepOptions,
} from '../dictate-onboarding.types';
import { useDictateOnboardingPrefill } from '../hooks/useDictateOnboardingPrefill';
import {
  clearPersistedOnboardingState,
  hotkeyDataToSelectedChord,
  loadPersistedOnboardingState,
  patchPersistedOnboardingState,
  selectedHotkeyToHotkeyData,
} from '../utils/dictateOnboardingPersistence';
import { DictateOnboardingContext } from './DictateOnboardingContext';

interface DictateOnboardingProviderProps {
  children: React.ReactNode;
  variant?: DictateOnboardingVariant;
  onComplete: () => void;
}

export function DictateOnboardingProvider({
  children,
  variant = 'full',
  onComplete,
}: DictateOnboardingProviderProps) {
  const isLite = variant === 'lite';
  const analytics = useDictateOnboardingAnalytics();
  const allGrantedAtOpen = useRef(useAtomValue(desktopDictationAllGrantedAtom));
  // Freeze initial eligibility. Permission grants during this step must not make
  // the wizard reconcile away before the user chooses Continue.
  const needsPermissions = isMac && !allGrantedAtOpen.current;

  const [formData, setFormDataState] = useState<DictateFormData>(() =>
    getInitialDictateFormData()
  );
  const [isCapturingHotkey, setIsCapturingHotkey] = useState(false);
  const [capturedChord, setCapturedChord] = useState<string[] | null>(null);
  const [selectedHotkey, setSelectedHotkeyState] = useState<string[] | null>(null);
  const [keySelectionHasValidHotkey, setKeySelectionHasValidHotkey] = useState(false);
  const [hasRestoredFromDisk, setHasRestoredFromDisk] = useState(isLite);

  const restoredStepRef = useRef<DictateStep>('permissions');
  const hasTrackedStartRef = useRef(false);
  const previousStepRef = useRef<DictateStep | null>(null);

  const wizardState = useWizard(
    isLite ? dictateOnboardingLiteWizard : dictateOnboardingWizard,
    isLite
      ? {
          guards: {
            needsPermissions: () => needsPermissions,
          },
        }
      : {
          // Restore-only adapter: the persisted document couples the step with the
          // hotkey, so the provider stays its single writer (the patch effect below).
          persistence: {
            restore: async () => {
              try {
                const restored = await loadPersistedOnboardingState();
                restoredStepRef.current = restored?.currentStep ?? 'permissions';
                if (restored?.hotkey) {
                  setSelectedHotkeyState(hotkeyDataToSelectedChord(restored.hotkey));
                }
                // The legacy disk document has no history, so a resumed session
                // backs along the effective list (+ backTarget overrides) instead
                // of the visited path — identical for today's linear pre-auth
                // steps. Persist `history` here if a non-linear step is ever added.
                return restored?.currentStep
                  ? {
                      step: restored.currentStep,
                      completed: [],
                      history: [],
                      done: false,
                    }
                  : null;
              } catch (error) {
                logger.error(
                  '[DictateOnboarding] Failed to restore persisted onboarding state',
                  { error }
                );
                // Fail open: a rejected restore must still release the gate so token
                // minting (gated on `hasRestoredFromDisk`) isn't permanently blocked —
                // we simply stay on the default step.
                return null;
              } finally {
                setHasRestoredFromDisk(true);
              }
            },
            clear: () => clearPersistedOnboardingState(),
          },
        }
  );

  const currentStep = wizardState.step;
  const wizardRef = useRef(wizardState);
  wizardRef.current = wizardState;

  useEffect(() => {
    if (!hasRestoredFromDisk || hasTrackedStartRef.current) return;

    hasTrackedStartRef.current = true;
    analytics.trackOnboardingStarted({
      resumedFromDisk: isLite ? false : restoredStepRef.current !== 'permissions',
    });
    // Lite keeps the legacy modal screen name so existing dashboards keep working.
    analytics.trackScreenViewed(
      isLite ? ScreenName.CURSOR_DICTATION_ONBOARDING_MODAL : ScreenName.DictateOnboarding
    );
  }, [analytics, hasRestoredFromDisk, isLite]);

  useEffect(() => {
    if (!hasRestoredFromDisk) return;

    if (previousStepRef.current === null) {
      previousStepRef.current = currentStep;
      return;
    }

    if (previousStepRef.current !== currentStep) {
      analytics.resetStepTimer();
      previousStepRef.current = currentStep;
    }
  }, [analytics, currentStep, hasRestoredFromDisk]);

  useEffect(() => {
    if (isLite) return;
    if (!hasRestoredFromDisk) return;
    if (!PRE_AUTH_STEP_SET.has(currentStep)) return;

    const isPristine = currentStep === 'permissions' && selectedHotkey === null;
    if (isPristine) return;

    void patchPersistedOnboardingState({
      currentStep,
      hotkey: selectedHotkeyToHotkeyData(selectedHotkey),
    });
  }, [hasRestoredFromDisk, currentStep, selectedHotkey, isLite]);

  const setFormData = useCallback((patch: Partial<DictateFormData>) => {
    setFormDataState((prev) => ({ ...prev, ...patch }));
  }, []);

  useDictateOnboardingPrefill(formData, setFormData);

  const setSelectedHotkey = useCallback((chord: string[] | null) => {
    setSelectedHotkeyState(chord);
  }, []);

  const goToNextStep = useCallback(
    (extra?: DictateStepCompletedExtra, options?: GoToNextStepOptions) => {
      const snapshot = wizardRef.current;
      // The wizard's done-state is intentionally never reached through next():
      // completion is consumer-owned via completeOnboarding() (the
      // ready-to-dictate CTA), so this guard stops next() short of the
      // terminal transition — parity with the old `getNextStep(...) === null`.
      if (snapshot.stepNumber >= snapshot.totalSteps) return;

      // `snapshot` is the pre-navigation snapshot, so step/stepNumber below
      // are the completed step's values; track only for a taken transition.
      if (!snapshot.next()) return;
      if (!options?.skipStepCompletion) {
        analytics.trackStepCompleted(snapshot.step, snapshot.stepNumber, extra);
      }
    },
    [analytics]
  );

  const goToPreviousStep = useCallback(() => {
    wizardRef.current.back();
  }, []);

  const goToStep = useCallback(
    (step: DictateStep, extra?: DictateStepCompletedExtra) => {
      const snapshot = wizardRef.current;
      if (snapshot.step === step) return;

      if (isLite) {
        const order = dictateOnboardingLiteWizard.stepIds;
        if (!order.includes(step as DictateLiteStep)) return;

        const from = snapshot.step as DictateLiteStep;
        const isForward = order.indexOf(step as DictateLiteStep) > order.indexOf(from);
        // Track only when the wizard confirms the transition was taken — a
        // refused goTo (e.g. a disabled target) must not emit a phantom
        // step-completed event.
        if (snapshot.goTo(step) && isForward) {
          analytics.trackStepCompleted(snapshot.step, snapshot.stepNumber, extra);
        }
        return;
      }

      const order = dictateOnboardingWizard.stepIds;
      const isForward = order.indexOf(step) > order.indexOf(snapshot.step);
      // Track only when the wizard confirms the transition was taken — a
      // refused goTo (e.g. a disabled target) must not emit a phantom
      // step-completed event.
      if (snapshot.goTo(step) && isForward) {
        analytics.trackStepCompleted(snapshot.step, snapshot.stepNumber, extra);
      }
    },
    [analytics, isLite]
  );

  const completeCurrentStep = useCallback(
    (extra?: DictateStepCompletedExtra) => {
      const snapshot = wizardRef.current;
      analytics.trackStepCompleted(snapshot.step, snapshot.stepNumber, extra);
    },
    [analytics]
  );

  const completeOnboarding = useCallback(() => {
    // Drop the cumulative analytics session so a re-entry starts a fresh funnel.
    // Covers the exit-without-completion path (the success path also clears it
    // from `trackOnboardingCompleted`).
    clearAnalyticsSession();
    if (isLite) {
      onComplete();
      return;
    }
    // Clear transient resume data first; `onComplete` then writes the durable
    // `has_completed_dictation_onboarding` flag, so the two same-file writes are
    // ordered rather than racing.
    void Promise.resolve(clearPersistedOnboardingState()).finally(() => {
      onComplete();
    });
  }, [isLite, onComplete]);

  const value = useMemo<DictateOnboardingContextValue>(
    () => ({
      variant,
      currentStep,
      formData,
      progressPercentage: Math.round(wizardState.progress * 100),
      canGoBack: wizardState.canGoBack,
      isCapturingHotkey,
      capturedChord,
      selectedHotkey,
      keySelectionHasValidHotkey,
      hasRestoredFromDisk,
      setFormData,
      setIsCapturingHotkey,
      setCapturedChord,
      setSelectedHotkey,
      setKeySelectionHasValidHotkey,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      completeOnboarding,
      completeCurrentStep,
      analytics: {
        trackStepSkipped: analytics.trackStepSkipped,
        trackOnboardingCompleted: analytics.trackOnboardingCompleted,
      },
    }),
    [
      variant,
      currentStep,
      formData,
      wizardState.progress,
      wizardState.canGoBack,
      isCapturingHotkey,
      capturedChord,
      selectedHotkey,
      keySelectionHasValidHotkey,
      hasRestoredFromDisk,
      setFormData,
      setSelectedHotkey,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      completeOnboarding,
      completeCurrentStep,
      analytics.trackStepSkipped,
      analytics.trackOnboardingCompleted,
    ]
  );

  return (
    <DictateOnboardingContext.Provider value={value}>
      {children}
    </DictateOnboardingContext.Provider>
  );
}
